import CustomImage from "@/components/Atoms/Image";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import { MultiPurposeTitle } from "@/components/Atoms/Title";
import { VariantProps, cva } from "class-variance-authority";
import styles from "./index.module.scss";
import { useState } from "react";
import { ButtonMaster } from "@/components/Atoms/Buttons";
import { ContactInfoCard } from "@/components/Molecules/ContactInfoCard";

// Helper to detect entry type based on fields
const isContactRecord = (entry: any): boolean => {
	return (
		entry.isEContacts !== undefined ||
		entry.isPhysicalAdress !== undefined ||
		entry.isSchedule !== undefined ||
		entry.email !== undefined ||
		entry.telephone !== undefined ||
		entry.mobile !== undefined ||
		entry.timeFrames !== undefined ||
		entry.googleMapsLocation !== undefined
	);
};

export type BlockVariantProps = VariantProps<typeof BlockVariants>;
const BlockVariants = cva(
	` grid container mx-auto gap-4 lg:gap-8 pb-6 2xl:max-w-[1360px]`,
	{
		variants: {
			gridDisplay: {
				half: `grid-cols-2`,
				third: `grid-cols-1 md:grid-cols-3 `,
				quarter: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
			},
		},
		defaultVariants: {
			gridDisplay: "third",
		},
	}
);

interface Props {
	__typename: string;
	composer: any;
	backgroundColor: any;
	gridDisplay: "half" | "third" | "quarter";
	textColor: any;
	titleColor: any;
	titleType: "default" | "highlight";
	textType: "default" | "highlight";
	addHeader: boolean;
	title: string;
	label: string;
	composerType: "default" | "people" | "contact";
	loadButtonConfig: any;
	iconAlign?: "default" | "row";
}

export default function MultiPurposeBlock({
	composer,
	backgroundColor,
	textColor,
	gridDisplay,
	titleType,
	textType,
	titleColor,
	title,
	label,
	addHeader,
	composerType,
	loadButtonConfig,
	iconAlign = "default",
}: Props) {
	const [visibleItems, setVisibleItems] = useState(12);

	function loadMore() {
		setVisibleItems((prev) => {
			const newCount = prev + 12;

			return newCount;
		});
	}

	const itemsToShow =
		composerType === "people" ? composer?.slice(0, visibleItems) : composer;

	const hasMoreItems =
		composerType === "people" && composer?.length > visibleItems;

	// Render function for each entry type
	const renderEntry = (entry: any, index: number) => {
		// Auto-detect ContactRecord entries regardless of composerType
		if (isContactRecord(entry)) {
			return (
				<ContactInfoCard
					key={index}
					entry={entry}
					textColor={textColor}
					titleColor={titleColor}
					titleType={titleType}
					iconAlign={iconAlign}
				/>
			);
		}

		// Handle other types based on composerType
		switch (composerType) {
			case "people":
				return (
					<div
						key={index}
						className="flex flex-col gap-4 justify-start items-start text-left"
					>
						{entry.image && (
							<div className="relative overflow-hidden w-full">
								<CustomImage
									data={entry.image}
									className={`w-full h-full object-cover aspect-square ${styles.image}`}
								/>
							</div>
						)}
						<div className="flex flex-col">
							{entry.title && (
								<MultiPurposeParagraph
									type="headline"
									data={entry.title}
									color={textColor?.color}
								/>
							)}
							{entry.subtitle && (
								<MultiPurposeParagraph
									type="label"
									data={entry.subtitle}
									color={titleColor?.color}
								/>
							)}
						</div>

						{entry.text && (
							<div className="max-w-xs">
								<MultiPurposeParagraph
									type="default"
									data={entry.text}
									color={textColor?.color}
								/>
							</div>
						)}
					</div>
				);

			case "default":
			default:
				// Icon element for TextWithImageRecord
				const IconElement = entry.image?.url && (
					<div className="w-8 h-8 lg:w-10 lg:h-10 relative flex-shrink-0">
						<CustomImage data={entry.image} />
					</div>
				);

				// Title element
				const TitleElement = entry.title && (
					<MultiPurposeParagraph
						type={titleType === "default" ? "headline" : "headlineFeatured"}
						data={entry.title}
						color={titleColor?.color}
					/>
				);

				return (
					<div
						key={index}
						className="flex flex-col gap-4 justify-start items-start text-left"
					>
						{/* Icon and Title - row or default layout */}
						{iconAlign === "row" ? (
							<div className="flex flex-row items-center justify-start gap-4">
								{IconElement}
								{TitleElement}
							</div>
						) : (
							<>
								{IconElement}
								{TitleElement}
							</>
						)}

						{/* Subtitle */}
						{entry.subtitle && (
							<MultiPurposeParagraph
								type={titleType === "default" ? "default" : "featuredText"}
								data={entry.subtitle}
								color={titleColor?.color}
							/>
						)}

						{/* Text content */}
						{entry.text && (
							<div className="max-w-full">
								<MultiPurposeParagraph
									type={textType === "default" ? "default" : "headline"}
									data={entry.text}
									color={textColor?.color}
								/>
							</div>
						)}
					</div>
				);
		}
	};

	return (
		<div
			style={{ backgroundColor: backgroundColor?.color.hex }}
			className={`py-8 md:py-20`}
		>
			{addHeader && (
				<div className="container mx-auto text-center mb-12 gap-4 max-w-3xl">
					{label && (
						<MultiPurposeParagraph
							type="default"
							data={label}
							color={titleColor.color}
						/>
					)}
					<MultiPurposeTitle
						type="default"
						tag="h3"
						data={title}
						color={titleColor?.color}
						columns={"single"}
					/>
				</div>
			)}
			<div className={BlockVariants({ gridDisplay })}>
				{itemsToShow?.map((entry: any, index: number) => renderEntry(entry, index))}
			</div>

			{hasMoreItems && (
				<div className="flex justify-center my-6 lg:mt-12">
					<ButtonMaster
						onClick={loadMore}
						anim={"slideLeft"}
						type={loadButtonConfig.buttonStyle}
						cta={loadButtonConfig.typeColor?.color}
						color={loadButtonConfig.labelColor?.color}
						customIcon={loadButtonConfig?.icon?.img}
						customColor={loadButtonConfig?.iconColor?.color}
						target=""
					>
						{loadButtonConfig?.buttonLabel?.label}
					</ButtonMaster>
				</div>
			)}
		</div>
	);
}

export const fragment = `
    fragment SectionMultiPurposeBlock on SectionMultiPurposeBlockRecord{
        __typename
        id
		iconAlign
        backgroundColor {
          color {
            hex
          }
        }
        textColor {
          color {
            hex
          }
        }
        titleColor {
          color {
            hex
          }
        }
        loadButtonConfig {
          ...pageRelatedFragment
        }
        titleType
        textType
        gridDisplay
        addHeader
        title
        label
        composerType
        iconAlign
        composer {
          ... on TextRecord {
            __typename
            id
            addTitle
            addSubtitle
            subtitle
            title
            text
          }
          ... on TextWithImageRecord {
            __typename
            id
            title
            addSubtitle
            subtitle
            text
            image {
              url
              focalPoint {
                y
                x
              }
              responsiveImage (imgixParams: {q: "65"}) {
                ...responsiveImageFragment
              }
            }
          }
          ... on ContactRecord {
            __typename
            id
            isEContacts
            isPhysicalAdress
            isSchedule
            title
            email
            googleMapsLocation
            address
            mobile
            telephone
            timeFrames
            icon {
              url
              alt
            }
          }
        }
    }
`;
